import { apiClient } from "@/api/client";
import type { publicOperatorAdmin } from "@kintone-storage/openapi-types";
import {
  STORAGE_BROWSE_RESULT_ROUTE,
  STORAGE_BROWSE_WIZARD_STATE_KEY,
  STORAGE_BROWSE_WIZARD_STATE_VERSION,
} from "@/types/storage-registration";
import type {
  StorageBrowseItemSummary,
  StorageBrowseWizardState,
  StorageConnectionKindId,
  StorageConnectionKindsResponse,
} from "@/types/storage-registration";
import { requiredIdempotencyHeaders } from "./command-idempotency";
import { organizationScopedSessionStorageKey } from "@/lib/organization-scope";

type GeneratedStorageSummary =
  publicOperatorAdmin.components["schemas"]["StorageSummary"];
type GeneratedStorageBrowseSessionResponse =
  publicOperatorAdmin.components["schemas"]["StorageBrowseSessionResponse"];

export type StorageSummary = GeneratedStorageSummary;
export type StorageBrowseSessionResponse = GeneratedStorageBrowseSessionResponse;

export async function fetchConnectionKinds(): Promise<StorageConnectionKindsResponse> {
  return apiClient<StorageConnectionKindsResponse>("/connection-kinds");
}

export async function startStorageBrowseSession(input: {
  microsoftConnectionMode?: "standard" | "site_search";
  connectionKindId: StorageConnectionKindId;
  storageDisplayName: string;
}): Promise<StorageBrowseSessionResponse> {
  return apiClient<StorageBrowseSessionResponse>("/console-bff/storage-browse-sessions", {
    method: "POST",
    body: JSON.stringify({
      connectionKindId: input.connectionKindId,
      ...(input.microsoftConnectionMode ? { microsoftConnectionMode: input.microsoftConnectionMode } : {}),
      registrationProcedure: "browse_select_cloud",
      storageDisplayName: input.storageDisplayName,
    }),
  });
}

export function redirectToStorageBrowse(url: string) {
  window.location.assign(url);
}

export async function listStorageBrowseSessionItems(
  browseSessionId: string,
  parentRef?: string,
): Promise<{ items: StorageBrowseItemSummary[]; connectedAccountLabel?: string }> {
  return apiClient<{ items: StorageBrowseItemSummary[]; connectedAccountLabel?: string }>(
    `/console-bff/storage-browse-sessions/${browseSessionId}/list`,
    {
      method: "POST",
      body: JSON.stringify(parentRef ? { parentRef } : {}),
    },
  );
}

export async function getStorageBrowseSessionContext(
  browseSessionId: string,
): Promise<StorageBrowseWizardState> {
  return apiClient<StorageBrowseWizardState>(
    `/console-bff/storage-browse-sessions/${browseSessionId}/context`,
  );
}

export async function startStoragePicker(browseSessionId: string): Promise<{
  pickerSessionId: string;
  pickerUrl: string;
  expiresAt: string;
}> {
  return apiClient(`/console-bff/storage-browse-sessions/${browseSessionId}/picker`, {
    method: "POST",
    body: JSON.stringify({}),
  });
}

export async function resolveStorageBrowseSessionLinks(
  browseSessionId: string,
  rawLocator: string,
): Promise<{
  candidateRef: string;
  valid: boolean;
  entryRef: string | null;
  displayName?: string | null;
}> {
  return apiClient<{
    candidateRef: string;
    valid: boolean;
    entryRef: string | null;
    displayName?: string | null;
  }>(`/console-bff/storage-browse-sessions/${browseSessionId}/resolve-links`, {
    method: "POST",
    body: JSON.stringify({ rawLocator }),
  });
}

export async function verifyStorageBrowseSessionCandidate(
  browseSessionId: string,
  candidateRef: string,
): Promise<{
  candidateRef: string;
  valid: boolean;
  entryRef: string | null;
  displayName?: string | null;
}> {
  return apiClient<{
    candidateRef: string;
    valid: boolean;
    entryRef: string | null;
    displayName?: string | null;
  }>(`/console-bff/storage-browse-sessions/${browseSessionId}/verify-candidates`, {
    method: "POST",
    body: JSON.stringify({ candidateRef }),
  });
}

export async function selectStorageBrowseSession(input: {
  browseSessionId: string;
  browseItemRef: string;
  idempotencyKey: string;
}): Promise<StorageSummary> {
  return apiClient<StorageSummary>(
    `/console-bff/storage-browse-sessions/${input.browseSessionId}/selection`,
    {
      method: "POST",
      headers: requiredIdempotencyHeaders(input.idempotencyKey),
      body: JSON.stringify({ browseItemRef: input.browseItemRef }),
    },
  );
}

export async function revokeStorageBrowseSession(
  browseSessionId: string,
): Promise<{ browseSessionId: string; status: string }> {
  return apiClient<{ browseSessionId: string; status: string }>(
    `/console-bff/storage-browse-sessions/${browseSessionId}/revoke`,
    {
      method: "POST",
      body: JSON.stringify({}),
    },
  );
}

export function readStorageBrowseWizardState(
  organizationScopeId?: string | null,
): StorageBrowseWizardState | null {
  const scopedStorageKey = organizationScopedSessionStorageKey(
    STORAGE_BROWSE_WIZARD_STATE_KEY,
    organizationScopeId,
  );
  if (!scopedStorageKey) return null;
  const raw = window.sessionStorage.getItem(scopedStorageKey);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as StorageBrowseWizardState | null;
    if (
      !parsed ||
      typeof parsed !== "object" ||
      parsed.version !== STORAGE_BROWSE_WIZARD_STATE_VERSION ||
      typeof parsed.browseSessionId !== "string" ||
      typeof parsed.connectionKindId !== "string" ||
      typeof parsed.selectedProcedure !== "string"
    ) {
      window.sessionStorage.removeItem(scopedStorageKey);
      return null;
    }
    return parsed;
  } catch {
    window.sessionStorage.removeItem(scopedStorageKey);
    return null;
  }
}

export function writeStorageBrowseWizardState(
  state: StorageBrowseWizardState,
  organizationScopeId?: string | null,
) {
  const scopedStorageKey = organizationScopedSessionStorageKey(
    STORAGE_BROWSE_WIZARD_STATE_KEY,
    organizationScopeId,
  );
  if (!scopedStorageKey) return;
  window.sessionStorage.setItem(
    scopedStorageKey,
    JSON.stringify({
      ...state,
      version: STORAGE_BROWSE_WIZARD_STATE_VERSION,
    }),
  );
}

export function clearStorageBrowseWizardState(
  organizationScopeId?: string | null,
) {
  const scopedStorageKey = organizationScopedSessionStorageKey(
    STORAGE_BROWSE_WIZARD_STATE_KEY,
    organizationScopeId,
  );
  if (!scopedStorageKey) return;
  window.sessionStorage.removeItem(scopedStorageKey);
}

export function buildStorageBrowseResultUrl(params: {
  browseSessionId: string;
  outcome: string;
  retryable: boolean;
}) {
  const search = new URLSearchParams({
    browseSessionId: params.browseSessionId,
    outcome: params.outcome,
    retryable: String(params.retryable),
  });
  return `${STORAGE_BROWSE_RESULT_ROUTE}?${search.toString()}`;
}
